/* eslint-disable global-require, no-underscore-dangle */
import React from 'react'
// import ReactDOM from 'react-dom'
import { createRoot } from 'react-dom/client'
import createStore from 'store/createStore'
import Emitter from 'events'
import { ApolloProvider } from '@apollo/client'
import { ModuleRegistry } from '@ag-grid-community/core'
import { LicenseManager } from '@ag-grid-enterprise/core'
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel'
import { SetFilterModule } from '@ag-grid-enterprise/set-filter'
import { RichSelectModule } from '@ag-grid-enterprise/rich-select'
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model'
import { SideBarModule } from '@ag-grid-enterprise/side-bar'
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model'
import { CsvExportModule } from '@ag-grid-community/csv-export'
import { FiltersToolPanelModule } from '@ag-grid-enterprise/filter-tool-panel'
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection'
import { ExcelExportModule } from '@ag-grid-enterprise/excel-export'
import { StatusBarModule } from '@ag-grid-enterprise/status-bar'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import duration from 'dayjs/plugin/duration'
import { initThirdPartyTrackersIfAllowed } from 'helpers/initThirdPartyTrackers'
import { initializeLDClient } from 'helpers/launchDarklyClient'

import '@ag-grid-community/styles/ag-grid.css'
import '@ag-grid-community/styles/ag-theme-alpine.css'

import { config as configureApiBuilder } from '@hixme/api'

// project
// import analytics from 'modules/analytics'
import client from 'apollo/client'
import appConfig from 'modules/app-config'
import AppContainer from 'containers/AppContainer'
import {
  config as authConfig,
  restoreLoginState,
  refreshAuth,
  selectors,
} from '@hixme/auth-ui'
import { config as configureEpsAuthUi } from '@surecompanies/auth-ui'
import {
  isMsalRedirectResponse,
  relayMsalRedirectResponse,
} from 'modules/sharepoint/redirectBridge'
import 'styles/core.scss'

import axios from 'axios'

LicenseManager.setLicenseKey(
  'Using_this_{AG_Grid}_Enterprise_key_{AG-080753}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{SureCo}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{SureCo_Application}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{SureCo_Application}_need_to_be_licensed___{SureCo_Application}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{1_September_2026}____[v3]_[01]_MTc4ODIxNzIwMDAwMA==7d76683fc5a1f22988b4bdc9a5e146f1'
)

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(duration)

ModuleRegistry.registerModules([
  ColumnsToolPanelModule,
  CsvExportModule,
  ClientSideRowModelModule,
  RichSelectModule,
  ServerSideRowModelModule,
  SideBarModule,
  SetFilterModule,
  FiltersToolPanelModule,
  RangeSelectionModule,
  ExcelExportModule,
  StatusBarModule,
])

// analytics.initialize({ga: 'UA-58480436-2'})

const stage = appConfig.getStage()

// auth setup
authConfig({
  stage, // auth module follows stage of app
})
configureEpsAuthUi({ stage })
initThirdPartyTrackersIfAllowed()

// emitter setup
const emitter = new Emitter()

// ========================================================
// Store Instantiation
// ========================================================
const initialState = window.__INITIAL_STATE__
let store = {}

const getAuthToken = () => ({
  Authorization: selectors.getToken(store.getState()),
})
const api = configureApiBuilder({ stage, getAuthToken })

store = createStore(initialState, { emitter, api })

// A SharePoint portal Microsoft sign-in popup returning to the app only relays
// its auth response to the opener; it must not restore the session or render.
const isMsalPopupResponse = isMsalRedirectResponse()

if (!isMsalPopupResponse) {
  // Restore and refresh the user's auth status
  store.dispatch(restoreLoginState())
  store.dispatch(refreshAuth())
}

// send auth token with each request
axios.interceptors.request.use(
  (config) => {
    const Authorization = selectors.getCredentials(
      store.getState() || {}
    ).idToken
    config.headers.Authorization = Authorization || ''
    return config
  },
  (error) => Promise.reject(error)
)
// ========================================================
// Render Setup
// ========================================================
const MOUNT_NODE = document.getElementById('root')

// for React 18 to work as React 18, uncomment when enzyme issues are fixed (no enzyme support for React 18)
const root = createRoot(MOUNT_NODE)

const render = async () => {
  const routes = require('./routes/index').default(store)

  // initialize LD Client for no react hook LD calls
  // I.E. in mapDispatchToProps etc
  try {
    await initializeLDClient()
  } catch (e) {
    console.error(e)
  }

  root.render(
    <ApolloProvider client={client}>
      <AppContainer emitter={emitter} store={store} routes={routes} />
    </ApolloProvider>
  )
}

if (isMsalPopupResponse) {
  relayMsalRedirectResponse().catch((e) => console.error(e))
} else {
  render()
}
